import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import billingService, { Proforma, ProformaCreateInput, ProformaUpdateInput, Bill, BillCreateInput, BillUpdateInput, RevenueSummary, MonthlyRevenue, TopClient } from '../../services/billingService';

// ==================== PROFORMAS SLICE ====================

interface ProformasState {
  proformas: Proforma[];
  currentProforma: Proforma | null;
  loading: boolean;
  error: string | null;
}

const initialProformasState: ProformasState = {
  proformas: [],
  currentProforma: null,
  loading: false,
  error: null,
};

export const fetchProformas = createAsyncThunk(
  'proformas/fetchProformas',
  async (userId: string) => {
    const response = await billingService.getProformas(userId);
    return response;
  }
);

export const fetchProformaById = createAsyncThunk(
  'proformas/fetchProformaById',
  async (params: { proformaId: number; userId: string }) => {
    const response = await billingService.getProformaById(params.proformaId, params.userId);
    return response;
  }
);

export const createProforma = createAsyncThunk(
  'proformas/createProforma',
  async (proformaData: ProformaCreateInput) => {
    const response = await billingService.createProforma(proformaData);
    return response;
  }
);

export const updateProforma = createAsyncThunk(
  'proformas/updateProforma',
  async (proformaData: ProformaUpdateInput) => {
    const response = await billingService.updateProforma(proformaData);
    return response;
  }
);

export const deleteProforma = createAsyncThunk(
  'proformas/deleteProforma',
  async (params: { proformaId: number; userId: string }) => {
    await billingService.deleteProforma(params.proformaId, params.userId);
    return params.proformaId;
  }
);

export const updateProformaStatusAction = createAsyncThunk(
  'proformas/updateProformaStatus',
  async (params: { proformaId: number; status: string; userId: string }) => {
    const response = await billingService.updateProformaStatus(params.proformaId, params.status, params.userId);
    return response;
  }
);

export const searchProformas = createAsyncThunk(
  'proformas/searchProformas',
  async (params: { userId: string; query: string }) => {
    const response = await billingService.searchProformas(params.userId, params.query);
    return response;
  }
);

const proformasSlice = createSlice({
  name: 'proformas',
  initialState: initialProformasState,
  reducers: {
    clearProformasError: (state) => {
      state.error = null;
    },
    clearCurrentProforma: (state) => {
      state.currentProforma = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Proformas
      .addCase(fetchProformas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProformas.fulfilled, (state, action) => {
        state.loading = false;
        state.proformas = action.payload;
      })
      .addCase(fetchProformas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch proformas';
      })
      // Fetch Proforma By ID
      .addCase(fetchProformaById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProformaById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProforma = action.payload;
      })
      .addCase(fetchProformaById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch proforma';
      })
      // Create Proforma
      .addCase(createProforma.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProforma.fulfilled, (state, action) => {
        state.loading = false;
        state.proformas.push(action.payload);
      })
      .addCase(createProforma.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create proforma';
      })
      // Update Proforma
      .addCase(updateProforma.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProforma.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.proformas.findIndex(proforma => proforma.id === action.payload.id);
        if (index !== -1) {
          state.proformas[index] = action.payload;
        }
      })
      .addCase(updateProforma.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update proforma';
      })
      // Delete Proforma
      .addCase(deleteProforma.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProforma.fulfilled, (state, action) => {
        state.loading = false;
        state.proformas = state.proformas.filter(proforma => proforma.id !== action.payload);
      })
      .addCase(deleteProforma.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete proforma';
      })
      // Update Proforma Status
      .addCase(updateProformaStatusAction.pending, (state) => {
        state.error = null;
      })
      .addCase(updateProformaStatusAction.fulfilled, (state, action) => {
        const index = state.proformas.findIndex(proforma => proforma.id === action.payload.id);
        if (index !== -1) {
          state.proformas[index] = action.payload;
        }
      })
      .addCase(updateProformaStatusAction.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update proforma status';
      })
      // Search Proformas
      .addCase(searchProformas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchProformas.fulfilled, (state, action) => {
        state.loading = false;
        state.proformas = action.payload;
      })
      .addCase(searchProformas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search proformas';
      });
  },
});

// ==================== BILLS SLICE ====================

interface BillsState {
  bills: Bill[];
  currentBill: Bill | null;
  loading: boolean;
  error: string | null;
}

const initialBillsState: BillsState = {
  bills: [],
  currentBill: null,
  loading: false,
  error: null,
};

export const fetchBills = createAsyncThunk(
  'bills/fetchBills',
  async (userId: string) => {
    const response = await billingService.getBills(userId);
    return response;
  }
);

export const fetchBillById = createAsyncThunk(
  'bills/fetchBillById',
  async (params: { billId: number; userId: string }) => {
    const response = await billingService.getBillById(params.billId, params.userId);
    return response;
  }
);

export const createBill = createAsyncThunk(
  'bills/createBill',
  async (billData: BillCreateInput) => {
    const response = await billingService.createBill(billData);
    return response;
  }
);

export const updateBill = createAsyncThunk(
  'bills/updateBill',
  async (billData: BillUpdateInput) => {
    const response = await billingService.updateBill(billData);
    return response;
  }
);

export const deleteBill = createAsyncThunk(
  'bills/deleteBill',
  async (params: { billId: number; userId: string }) => {
    await billingService.deleteBill(params.billId, params.userId);
    return params.billId;
  }
);

export const updateBillStatusAction = createAsyncThunk(
  'bills/updateBillStatus',
  async (params: { billId: number; status: string; userId: string }) => {
    const response = await billingService.updateBillStatus(params.billId, params.status, params.userId);
    return response;
  }
);

export const markBillAsPaidAction = createAsyncThunk(
  'bills/markBillAsPaid',
  async (params: { billId: number; paymentMethod: string; paymentDate: string }) => {
    const response = await billingService.markBillAsPaid(params.billId, params.paymentMethod, params.paymentDate);
    return response;
  }
);

export const sendBillAction = createAsyncThunk(
  'bills/sendBill',
  async (billId: number) => {
    const response = await billingService.sendBill(billId);
    return response;
  }
);

export const searchBills = createAsyncThunk(
  'bills/searchBills',
  async (params: { userId: string; query: string }) => {
    const response = await billingService.searchBills(params.userId, params.query);
    return response;
  }
);

// ==================== REVENUE SLICE ====================

interface RevenueState {
  summary: RevenueSummary | null;
  monthlyRevenue: MonthlyRevenue[];
  topClients: TopClient[];
  loading: boolean;
  error: string | null;
}

const initialRevenueState: RevenueState = {
  summary: null,
  monthlyRevenue: [],
  topClients: [],
  loading: false,
  error: null,
};

export const fetchRevenueSummary = createAsyncThunk(
  'revenue/fetchRevenueSummary',
  async (params: { userId: string; year?: string }) => {
    const response = await billingService.getRevenueSummary(params.userId, params.year);
    return response;
  }
);

export const fetchMonthlyRevenue = createAsyncThunk(
  'revenue/fetchMonthlyRevenue',
  async (params: { userId: string; year?: string }) => {
    const response = await billingService.getMonthlyRevenue(params.userId, params.year);
    return response;
  }
);

export const fetchTopClients = createAsyncThunk(
  'revenue/fetchTopClients',
  async (params: { userId: string; year?: string }) => {
    const response = await billingService.getTopClients(params.userId, params.year);
    return response;
  }
);

const billsSlice = createSlice({
  name: 'bills',
  initialState: initialBillsState,
  reducers: {
    clearBillsError: (state) => {
      state.error = null;
    },
    clearCurrentBill: (state) => {
      state.currentBill = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Bills
      .addCase(fetchBills.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBills.fulfilled, (state, action) => {
        state.loading = false;
        state.bills = action.payload;
      })
      .addCase(fetchBills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch bills';
      })
      // Fetch Bill By ID
      .addCase(fetchBillById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBill = action.payload;
      })
      .addCase(fetchBillById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch bill';
      })
      // Create Bill
      .addCase(createBill.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBill.fulfilled, (state, action) => {
        state.loading = false;
        state.bills.push(action.payload);
      })
      .addCase(createBill.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create bill';
      })
      // Update Bill
      .addCase(updateBill.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBill.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.bills.findIndex(bill => bill.id === action.payload.id);
        if (index !== -1) {
          state.bills[index] = action.payload;
        }
      })
      .addCase(updateBill.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update bill';
      })
      // Delete Bill
      .addCase(deleteBill.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBill.fulfilled, (state, action) => {
        state.loading = false;
        state.bills = state.bills.filter(bill => bill.id !== action.payload);
      })
      .addCase(deleteBill.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete bill';
      })
      // Update Bill Status
      .addCase(updateBillStatusAction.pending, (state) => {
        state.error = null;
      })
      .addCase(updateBillStatusAction.fulfilled, (state, action) => {
        const index = state.bills.findIndex(bill => bill.id === action.payload.id);
        if (index !== -1) {
          state.bills[index] = action.payload;
        }
      })
      .addCase(updateBillStatusAction.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update bill status';
      })
      // Mark Bill as Paid
      .addCase(markBillAsPaidAction.pending, (state) => {
        state.error = null;
      })
      .addCase(markBillAsPaidAction.fulfilled, (state, action) => {
        const index = state.bills.findIndex(bill => bill.id === action.payload.id);
        if (index !== -1) {
          state.bills[index] = action.payload;
        }
      })
      .addCase(markBillAsPaidAction.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to mark bill as paid';
      })
      // Send Bill
      .addCase(sendBillAction.pending, (state) => {
        state.error = null;
      })
      .addCase(sendBillAction.fulfilled, (state, action) => {
        const index = state.bills.findIndex(bill => bill.id === action.payload.id);
        if (index !== -1) {
          state.bills[index] = action.payload;
        }
      })
      .addCase(sendBillAction.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to send bill';
      })
      // Search Bills
      .addCase(searchBills.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchBills.fulfilled, (state, action) => {
        state.loading = false;
        state.bills = action.payload;
      })
      .addCase(searchBills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search bills';
      });
  },
});

const revenueSlice = createSlice({
  name: 'revenue',
  initialState: initialRevenueState,
  reducers: {
    clearRevenueError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Revenue Summary
      .addCase(fetchRevenueSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRevenueSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.summary = action.payload.data;
      })
      .addCase(fetchRevenueSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch revenue summary';
      })
      // Fetch Monthly Revenue
      .addCase(fetchMonthlyRevenue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMonthlyRevenue.fulfilled, (state, action) => {
        state.loading = false;
        state.monthlyRevenue = action.payload.data;
      })
      .addCase(fetchMonthlyRevenue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch monthly revenue';
      })
      // Fetch Top Clients
      .addCase(fetchTopClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopClients.fulfilled, (state, action) => {
        state.loading = false;
        state.topClients = action.payload.data;
      })
      .addCase(fetchTopClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch top clients';
      });
  },
});

export const { clearProformasError, clearCurrentProforma } = proformasSlice.actions;
export const { clearBillsError, clearCurrentBill } = billsSlice.actions;
export const { clearRevenueError } = revenueSlice.actions;

export { proformasSlice, billsSlice, revenueSlice };
